import React, { useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import * as faceapi from "face-api.js";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { Box, Typography, Button, IconButton, Tooltip, CircularProgress } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import ArrowBackButton from "../Base/views/ArrowBackButton";
import history from "../../../history";
import { addDistanceLogHelper, addFaceDataHelper } from "./helper/FaceRecognizationHelper";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { GetObjectCommand } from "@aws-sdk/client-s3";
import { getDomain, getEmployeeId, getUserData, s3Client } from "../Base/helper/baseFunctions";
import SuccessDialog from "./SuccessDialog";
import face_icon3 from "./images/face3.png";
import { checkLighting } from "../Base/views/FaceLightingChecker";
import { InitializeFaceApiModelsAction } from "../../../store/Ancillary/FaceApi/Action";
import { ToastContainer } from "react-toastify";
import BackdropMeepl from "../Ancillary/AnimationComponents/BackdropMeepl";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { GetAllEmployeesHelper, GetEmployeesHelper } from "../Employee/helper/EmployeeHelper";
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { Logout } from "@mui/icons-material";
import { signOut } from "../SignIn/helper/SigninHelper";

const Alert = ({ message, flag }) => {
  const containerStyle = {
    backgroundColor:
      flag === "success"
        ? "#b8dfc6"
        : flag === "detect"
          ? "#c4e8fa"
          : "#fde3de",
    borderRadius: "5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 15px",
    color: "#000",
    position: "relative",
    borderBottom:
      flag === "success"
        ? "1px solid #24965f"
        : flag === "detect"
          ? "1px solid #476b96"
          : "1px solid #c34638",
    marginBottom: "20px",
  };

  const infoIconStyle = {
    color:
      flag === "success"
        ? "#1b9857"
        : flag === "detect"
          ? "#3b649f"
          : "#bf4638",
    marginRight: "10px",
    fontSize: "18px",
  };

  const textStyle = {
    margin: 0,
    flex: 1,
    color: "#375640",
  };

  return (
    <div
      style={{
        width: "100%",
        textAlign: "center",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <div style={containerStyle}>
        <span style={infoIconStyle}>
          {flag === "success" ? (
            <CheckCircleIcon />
          ) : flag === "detect" ? (
            <AccessTimeFilledIcon />
          ) : (
            <ErrorIcon />
          )}
        </span>
        <p style={textStyle}>{message}</p>
      </div>
    </div>
  );
};

const FaceAttendanceTracker = () => {
  const dispatch = useDispatch();
  const webcamRef = useRef(null); // webCamera ref
  const successTimeoutRef = useRef(null); // Success Dialog Ok button click will stop the execution of setTimeOut
  const { user_id } = useParams();
  const paramsData = atob(user_id);
  const paramsDatas = paramsData?.split("=");
  const userId = paramsDatas?.[0]
  const navigationState = paramsDatas?.[1]

  const userInfo = getUserData();
  const [facingMode,] = useState("user");
  
  const [alertState, setAlertState] = useState({
    flag: "detect",
    message: "Detecting face..."
  });
  const updateAlert = (flag, message) => {
    setAlertState({flag, message});
  };

  const [attendanceWebcam, setAttendanceWebcam] = useState(false);
  
  const [openInitialDialog, setOpenInitialDialog] = useState(true);
  const [attendanceType, setAttendanceType] = useState("");
  const [successDialog, setSuccessDialog] = useState(false);
  
  const initialMarkedUserData = {
    imageUrl: "",
    employeeTime: "",
    detectedUser: null,
    entry_type: ""
  }
  const [markedUserData, setMarkedUserData] = useState(initialMarkedUserData);

  const [retry, setRetry] = useState(false);
  const [unknownRetry, setUnknownRetry] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRY = 1;
  const [markingAttendance, setMarkingAttendance] = useState(false);
// safe
  const [employeesOption, setEmployeeOptions] = useState([]);
  const [isEmployeeLoading, setEmployeeLoading] = useState(false);
  const { isLoading, isLoaded } = useSelector(
    (state) => state?.AncillaryReducer?.FaceApiReducer
  );
  const resetDefaultState = () => {
    updateAlert("detect", "Detecting face...");
    setMarkedUserData(initialMarkedUserData);
  }
  const resetInitialReset = () => {
    resetDefaultState();
    setAttendanceType("");
    setOpenInitialDialog(true);
    setSuccessDialog(false);
    setAttendanceWebcam(false);
    setMarkingAttendance(false);
  }
  const clearSuccessTimeout = () => {
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
  };
  const fetchAccessBasedEmployee = async() => {
    const apiCall = userInfo?.enable_face_location_restriction === true
      ? GetEmployeesHelper({ is_active: true, user_id: userId })
      : GetAllEmployeesHelper({ is_active: true });
    setEmployeeLoading(true);
    const resData = await apiCall;
    setEmployeeLoading(false);
    if(resData?.data?.statuscode === 200) {
      setEmployeeOptions(resData?.data?.data || [])
    }
  }
  useEffect(() => {
    if (!isLoaded) {
     dispatch(InitializeFaceApiModelsAction());
    }
    fetchAccessBasedEmployee();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const getImageSignedUrl = async (currentData) => {
    let filename = currentData?.image;
    const bucketParams = {
      Bucket: process.env.REACT_APP_DO_BUCKET_NAME,
      Key: filename,
    };
    try {
      const url = await getSignedUrl(
        s3Client,
        new GetObjectCommand(bucketParams),
        { expiresIn: 3 * 60 }
      ); // Adjustable expiration.
      return url;
    } catch (err) { }
  };
  const detectFace = async () => {
    updateAlert("detect", "Detecting face... please stay still.");
    if (webcamRef.current && webcamRef.current.video.readyState === 4) {
      const video = webcamRef.current.video;
      // ✅ Lighting check before detection
      const brightness = checkLighting(video);
      if (brightness < 55) {
        updateAlert("error", "Lighting is too low, please move to a brighter area.");
        setRetry(!retry); 
        return;
      }   
      const closeUpResult = await checkCloseUp(video);
      if (!closeUpResult?.pass) {
        updateAlert("error", closeUpResult?.message);
        setRetry(!retry);
        return;
      }
      try {
        const recognitionStartTime = Date.now(); // to check the recog time;
        const detections = await detectWithTimeout(video, 10000, 5, updateAlert);
        if (detections) {
          updateAlert("detect", "Analyzing captured face with employee database...");
          const registeredUsers = employeesOption?.filter(u => u?.descriptor) || [];
          const liveDescriptors = detections?.descriptor;

          let distanceLog = [];
            const distanceResults = registeredUsers?.map((user) => {
                const userDescriptorArray = new Float32Array(JSON.parse(user?.descriptor));

                if (userDescriptorArray?.length === liveDescriptors[0].length) {
                const frameDistances = liveDescriptors?.map(
                    (liveDescriptor) =>
                      faceapi.euclideanDistance(
                        userDescriptorArray,
                        liveDescriptor
                      )
                  );

                  const sortedDistances = [...frameDistances]?.sort(
                    (a, b) => a - b
                  );

                  const medianDistance = sortedDistances[Math.floor(sortedDistances?.length / 2)];

                  distanceLog.push(`${medianDistance} - Median Compare with - ${user?.name}`);

                  if (["xpohrmeepl", "cvtmeepl", "adobemeepl", "mixwbmeepl"].includes(getDomain())) {
                    console.log(`Compare with - ${user?.name}`,
                      {
                        frameDistances,
                        medianDistance
                      })}
                  return {user, distance: medianDistance, frameDistances};

                  }
                  if (["xpohrmeepl", "cvtmeepl", "adobemeepl"].includes(getDomain())) {
                    console.log(`Skipping ${user.name} — descriptor length mismatch:`, userDescriptorArray.length, "vs", detections.descriptor.length);
                  }
                  distanceLog.push(`${"null"} - Compare with - ${user?.name}`);
                  return null;
              })

            const validResults = distanceResults?.filter(r => r !== null);
            if (!validResults.length) {
              updateAlert("error", "No employee face data available.");
              setTimeout(() => {
                resetInitialReset();
              }, 1000);
              return;
            }

            const sortedResults = [...validResults].sort(
              (a, b) => a.distance - b.distance
            );
            const bestMatchResult = sortedResults[0];
            const secondBestMatchResult = sortedResults[1];

            const bestMatch = bestMatchResult?.user;
            const secondMatch = secondBestMatchResult?.user;

            const THRESHOLD = 0.52;
            const BEST_MATCH_THRESHOLD = 0.38;
            const MIN_GAP = 0.08;

            const truncateTo4 = (num) => Math.floor(num * 10000) / 10000;

            const bestDistance = truncateTo4(bestMatchResult?.distance);
            const secondBestDistance = secondBestMatchResult
              ? truncateTo4(secondBestMatchResult?.distance)
              : 999;

            const gap = truncateTo4(secondBestDistance - bestDistance);
            const getRecognitionTime = `Recog-Time: ${Date.now() - recognitionStartTime} ms`;

          if (bestMatch) {
            if (bestDistance > THRESHOLD) {
              if (!unknownRetry) {
                  setUnknownRetry(true);
                  updateAlert("error", "User is unknown. Retrying one more time...");
                  setRetry(!retry); 
              } else {
                updateAlert("error", "User is unknown");
                setUnknownRetry(false);
                setTimeout(() => {
                  resetInitialReset();
                }, 1000);
                addDistanceLogHelper({
                    emp_id: `User is unknown - bestDistance:${bestDistance}; ${getRecognitionTime}`,
                    name: `Loggged In device user-${getEmployeeId()}-${getUserData()?.name} - ${getDomain()}`,
                    distance: JSON.stringify(distanceLog),
                    userDescriptor: "No Match Found",
                    liveDescriptor: JSON.stringify(Array.from(detections.descriptor))
                })
              }
              return;
            }
            if (bestDistance < BEST_MATCH_THRESHOLD) {
              updateAlert("detect", `Match found! Verifying ${bestMatch.name}...`);
                if (["xpohrmeepl", "cvtmeepl", "adobemeepl", "mixwbmeepl"].includes(getDomain())) {
                  console.log(
                    `✅ Best match: ${bestMatch?.name} with distance ${bestDistance} (best_threshold ${bestDistance})`
                  );
                }
                setRetryCount(0);
                setUnknownRetry(false);
                setMarkedUserData((pre) => ({ ...pre, detectedUser: bestMatch }));
                markAttendance(bestMatch);
                addDistanceLogHelper({
                    emp_id: `Success:${bestDistance} - ${bestMatch?.employee_id}; BEST_MATCH_THRESHOLD; ${getRecognitionTime}`,
                    name: `${bestMatch?.name} - ${bestMatch?.office_email} - ${getDomain()}`,
                    distance: JSON.stringify(distanceLog),
                    userDescriptor: bestMatch?.descriptor,
                    liveDescriptor: JSON.stringify(Array.from(detections.descriptor))
                })
                return;
            }
            if (bestDistance < THRESHOLD) {
              if (gap < MIN_GAP) {
                if (retryCount < MAX_RETRY) {
                  setRetryCount(prev => prev + 1);

                  updateAlert(
                    "error",
                    "Face match is ambiguous. Retrying one more time..."
                  );
                  addDistanceLogHelper({
                    emp_id: `Ambiguous Match: bestDistance: ${bestDistance} - secondBestDistance: ${secondBestDistance} Gap=${gap}; ${getRecognitionTime}`,
                    name: `${bestMatch?.name} - ${secondMatch?.name} - ${getDomain()}`,
                    distance: JSON.stringify(distanceLog),
                    userDescriptor: bestMatch?.descriptor,
                    liveDescriptor: JSON.stringify(
                      Array.from(detections.descriptor)
                    ),
                  });
                  setRetry(prev => !prev);
                  return;
                }

                setRetryCount(0);
                updateAlert("error", "Unable to identify user. Please try again.");
                setTimeout(() => {
                  resetInitialReset();
                }, 1000);
                return;
              }
              updateAlert("detect", `Match found! Verifying ${bestMatch.name}...`);
              setRetryCount(0);
              setUnknownRetry(false);
              setMarkedUserData((pre) => ({
                ...pre,
                detectedUser: bestMatch,
              }));

              markAttendance(bestMatch);
              if (["xpohrmeepl", "cvtmeepl", "adobemeepl", "mixwbmeepl"].includes(getDomain())) {
                  console.log(
                    `✅ Best match: ${bestMatch?.name} with distance ${bestDistance}; bestDistance < THRESHOLD && gap'${gap}' > MIN_GAP '${MIN_GAP}'; retryCount:${retryCount})`
                  );
                }
              addDistanceLogHelper({
                emp_id: `Success:${bestDistance} - ${bestMatch?.employee_id}; bestDistance < THRESHOLD && gap'${gap}' > MIN_GAP '${MIN_GAP}'; retryCount:${retryCount}; ${getRecognitionTime}`,
                name: `${bestMatch?.name} - ${bestMatch?.office_email} - ${getDomain()}`,
                distance: JSON.stringify(distanceLog),
                userDescriptor: bestMatch?.descriptor,
                liveDescriptor: JSON.stringify(
                  Array.from(detections.descriptor)
                ),
              });
              return;
            }
          }
        } else {
          updateAlert("error", "No face detected. Trying again.");
          setRetry(!retry);
        }
    } catch (error) {
      if (error.message === "Detection timeout") {
        updateAlert("error", "Face detection took too long. Please hold still, retrying...");
        setRetry(!retry);
      } else {
        console.error("Detection failed:", error);
        updateAlert("error", "An unexpected error occurred during detection.");
        setRetry(!retry);
      }
    }
    } else {
      setRetry(!retry);
    }
  };

  const markAttendance = async (data) => {
    getImageSignedUrl(data).then((url) => {
      if (url) setMarkedUserData((pre) => ({...pre, imageUrl: url}));
    });
    const getLocalISOTime = () => {
      const now = new Date();
      const offsetInMinutes = now.getTimezoneOffset(); // Offset in minutes
      const localTime = new Date(now.getTime() - offsetInMinutes * 60000);
      return localTime.toISOString().slice(0, -1); // Remove the "Z" at the end
    };
    const record_time = getLocalISOTime();
    updateAlert("detect", `Marking attendance for ${data?.name}, please wait...`);
    setMarkingAttendance(true);
    const locationName = data?.location_name ? JSON.stringify({displayName: data?.location_name}) : ""
    addFaceDataHelper({
      ...data,
      record_time: record_time,
      location_name: locationName
    }).then((res) => {

      if (res?.data?.statuscode === 200) {
        const resData = res?.data?.data?.[0] || {};
        const currentAttendanceType = resData?.current_action || "";   
        setMarkedUserData((prev) => ({...prev, employeeTime: resData, entry_type: currentAttendanceType}))  
          
        setAttendanceWebcam(false);
        updateAlert("success", `Attendance marked for ${data?.name}`);
        setSuccessDialog(true);
        
        const delayTime = currentAttendanceType === "Check In" ? 3000 : 5000;
        clearSuccessTimeout();
        successTimeoutRef.current = setTimeout(() => {
          resetInitialReset();
          successTimeoutRef.current = null;
        }, delayTime);
      } else {
        resetInitialReset();
      }
    }).finally(() => {
      setMarkingAttendance(false);
    });
  };

  useEffect(() => {
    return () => {
      clearSuccessTimeout();
    };
  }, []);

  //stop user to navigate back from this page
  useEffect(() => {
    const unblock = history.block((location, action) => {
      if (action === "POP") {
        return false;
      }
    });

    return () => {
      unblock();
    };
  }, []);

  const handleDialogClose = (type) => {
    !employeesOption?.length > 0 &&
      fetchAccessBasedEmployee();
      updateAlert("detect", "Initializing face detection...");
    setAttendanceType(type);
    setOpenInitialDialog(false);
    setAttendanceWebcam(true);
  };

  useEffect(() => {
    if (
      employeesOption?.length > 0 &&
      attendanceType && !markingAttendance
    ) {
      const interval = setInterval(() => {
        if (webcamRef.current && webcamRef.current.video.readyState === 4) {
          // Lighting check
        const video = webcamRef.current.video;
        const brightness = checkLighting(video);
        if (brightness < 55) {
          updateAlert("error", "Lighting is too low, please move to a brighter area.");
          return; 
        } 
          clearInterval(interval);
          detectFace();
        }
      }, 1000);
      return () => clearInterval(interval);
    }
        // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeesOption, attendanceType, retry]);

  const switchToVisitorEntry = () => {
     history.push(`/visitors-management-common-portal`, "common-portal");
  }
  const handleBackClick = () => {
     history.push(`/common-portal`);
  }
  const handleSignout = () => {
    dispatch({ type: "USER_LOGOUT" });
    signOut();
  };
  return (
    <>
      <BackdropMeepl open={isEmployeeLoading || isLoading} dark={true}/>
      <Box
        className="bg_form_main"
        sx={{
          minHeight: "100vh",
          paddingTop: "20px",
          pb: "20px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "10px",
            right: "35px",
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
            color: "#000",
            "&:hover": {
              color: "#8b44ff",
            },
          }}
          onClick={handleSignout}
        >
          <Logout
            sx={{
              fontSize: "24px",
            }}
          />
          <Typography
            variant="body1"
            sx={{
              fontWeight: "bold",
              fontSize: "16px",
            }}
          >
            Sign Out
          </Typography>
        </Box>
        <Box
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            // backgroundColor: "red",
            backdropFilter: "blur(10px)",
            borderRadius: "10px",
            padding: "5px 20px",
            pb: attendanceWebcam ? "80px" : "15px",
            width: "100%",
            maxWidth: "660px",
            margin: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              margin: { xs: "10px auto", md: "0 auto" },
            }}
          >
            <Box sx={{ width: "1px" }}>
              {navigationState === "common-portal" &&
                <IconButton onClick={handleBackClick} edge="start">
                  <ArrowBackButton />
                </IconButton>
              }
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", margin: "auto" }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: "20px", sm: "36px", md: "28px" },
                  fontWeight: "bold",
                }}
              >
                Face Recognition Attendance
              </Typography>
            </Box>
            {navigationState === "common-portal" && <Box>
              <Tooltip title="Switch to Visitor Entry">
                <IconButton onClick={switchToVisitorEntry}>
                  <SyncAltIcon sx={{ fontSize: 24 }} />
                </IconButton>
              </Tooltip>
            </Box>}
          </Box>

          {openInitialDialog && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                mt: 4,
                mb: 4,
              }}
            >
              <Box
                component="img"
                src={face_icon3}
                alt="face_icon"
                sx={{
                  width: { xs: "150px", sm: "220px", md: "150px" },
                  height: { xs: "150px", sm: "220px", md: "150px" },
                  borderRadius: "10px",
                  mb: { xs: "40px", sm: "50px", md: "40px" },
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 4, sm: 6, md: 4 },
                  flexDirection: { xs: "column", md: "row" },
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    width: { xs: "340px", sm: "510px", md: "410px" },
                    height: { xs: "80px", sm: "120px", md: "55px" },
                    fontSize: { xs: "25px", sm: "40px", md: "30px" },
                    padding: 4,
                    color: "black",
                    fontWeight: "bold",
                    background: "#fecb03",
                    borderRadius: "10px",
                    border: "1px solid #fecb03",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      cursor: "pointer",
                      backgroundColor: "#fecb03",
                      boxShadow: "0px 4px 10px rgba(254,203,3, 0.3)",
                    },
                  }}
                  onClick={() => handleDialogClose("Check In")}
                >
                  Mark Attendance
                </Button>
              </Box>
            </Box>
          )}
          {attendanceWebcam && attendanceType &&  (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  textAlign: "center",
                  position: "relative",
                  width: "100%",
                  maxWidth: "640px",
                  height: "440px",
                  margin: "0 auto",
                }}
              >
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Webcam
                    ref={webcamRef}
                    audio={false}
                    screenshotFormat="image/jpeg"
                    videoConstraints={{
                      width: 640,
                      height: 480,
                      facingMode:facingMode,
                    }}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  {markingAttendance && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          zIndex: 2,
                          transform: "translate(-50%, -50%)",
                          background: "rgba(255,255,255,0.4)",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: { xs: 310, sm: 370, md: 390 },
                          height: { xs: 310, sm: 350, md: 390 },
                        }}
                      >
                        <CircularProgress
                          sx={{ color: "#a5adbc" }}
                          size={290}
                          thickness={1}
                          disableShrink
                        />
                      </Box>
                    )}
                 
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      pointerEvents: "none",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "70px",
                        height: "70px",
                        borderTop: "5px solid #00ff00",
                        borderLeft: "5px solid #00ff00",
                        borderRadius: "16px 0 0 0px",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: "70px",
                        height: "70px",
                        borderTop: "5px solid #00ff00",
                        borderRight: "5px solid #00ff00",
                        borderRadius: "0 16px 0 0",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        width: "70px",
                        height: "70px",
                        borderBottom: "5px solid #00ff00",
                        borderLeft: "5px solid #00ff00",
                        borderRadius: "0 0 0 16px",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        right: 0,
                        width: "70px",
                        height: "70px",
                        borderBottom: "5px solid #00ff00",
                        borderRight: "5px solid #00ff00",
                        borderRadius: "0 0 16px 0",
                      }}
                    />
                  </div>
                </div>
                {/* <Button variant="contained" onClick={toggleCamera} sx={{ mt: 2 }}>
                    Switch Camera
                  </Button> */}
                <div style={{ margin: "15px" }}>
                  {!attendanceWebcam ? (
                    <Alert message={alertState?.message} flag={alertState?.flag} />
                  ) : (
                    <Alert message={alertState?.message} flag={alertState?.flag} />
                  )}
                </div>
              </div>
            </Box>
          )}

          {successDialog && (
            <SuccessDialog
              handleClose={() => {
                resetInitialReset();
                clearSuccessTimeout();
              }}
              markedUserData={markedUserData}
            />
          )}
        </Box>
        <ToastContainer position="bottom-left" autoClose={2000} />
      </Box>
    </>
  );
};
// ----- Fallback detection with timeout -----
const detectWithTimeout = (video, ms = 10000, frames = 3, updateAlert= () =>{}) => {
  return Promise.race([
    detectMultiFrame(video, frames, updateAlert), 
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Detection timeout")), ms)
    ),
  ]);
};

// To detect blurry frames // skipped for now due to different devices type camera resolution
// eslint-disable-next-line 
const isBlurry = (video, threshold = 30) => {
  try {
    const maxW = 320;
    const scale = Math.min(1, maxW / (video.videoWidth || maxW));
    const w = Math.max(1, Math.floor((video.videoWidth || maxW) * scale));
    const h = Math.max(1, Math.floor((video.videoHeight || maxW * 0.75) * scale));

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

    const gray = new Float32Array((imageData.data.length / 4) | 0);
    let gi = 0;
    for (let i = 0; i < imageData.data.length; i += 4) {
      gray[gi++] = 0.299 * imageData.data[i] + 0.587 * imageData.data[i + 1] + 0.114 * imageData.data[i + 2];
    }

    const wN = canvas.width;
    const hN = canvas.height;
    const lap = [];
    for (let y = 1; y < hN - 1; y++) {
      for (let x = 1; x < wN - 1; x++) {
        const idx = y * wN + x;
        const value = 4 * gray[idx] - gray[idx - 1] - gray[idx + 1] - gray[idx - wN] - gray[idx + wN];
        lap.push(value);
      }
    }
    if (!lap.length) return false;
    const mean = lap.reduce((a, b) => a + b, 0) / lap.length;
    const variance = lap.reduce((a, b) => a + (b - mean) ** 2, 0) / lap.length;
    return variance < threshold;
  } catch (e) {
    console.warn("isBlurry failed, skipping blur check:", e);
    return false;
  }
};


// Detect face with multiple frames and blur check
const detectMultiFrame = async (video, frameCount = 3, updateAlert) => {
  const validDescriptors = [];
  let attempts = 0;

  while (attempts < frameCount * 10) {
    attempts++;

    if (video.readyState !== 4) break;

    const quality = await validateFaceQuality(video);
    if (quality?.valid) {
      const detection = await faceapi.detectSingleFace(
          video,
          new faceapi.TinyFaceDetectorOptions()
        )
        .withFaceLandmarks().withFaceDescriptor();

      if (detection) {
        validDescriptors.push(detection.descriptor);
        updateAlert("detect", "Detecting face... please stay still.");
        // Stop immediately when enough good frames found
        if (validDescriptors.length >= frameCount) {
          break;
        }
      }
    } else {
      updateAlert?.("error", quality?.message);
    }

    await new Promise((r) => setTimeout(r, 150));
  }
  if (!validDescriptors.length) return null;
  // return { descriptor: averageDescriptors(validDescriptors) };
  return { descriptor: validDescriptors };
};

const checkCloseUp = async (video) => {
  // Detect face in the frame
  if (!video?.videoHeight || !video?.videoWidth) {
    return { pass: false, message: "Initializing face detection..." };
  }
  const detections = await faceapi.detectAllFaces(
    video,
    new faceapi.TinyFaceDetectorOptions()
  );

  if (!detections.length) return { pass: false, message: "No face detected. Please align your face inside the frame." };

  const detection = detections[0];
  const box = detection.box;
  const faceRatio = box.height / video.videoHeight;

  // Define acceptable size range
  if (faceRatio < 0.28) {
    return { pass: false, message: "Move closer to the camera." };
  } else if (faceRatio > 0.85) {
    return { pass: false, message: "Move slightly back from the camera." };
  }

  return { pass: true, message: "Perfect! Face is properly aligned." };
};

const validateFaceQuality = async (video) => {
  const detection = await faceapi
    .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions())
    .withFaceLandmarks();

  if (!detection) {
    return {
      valid: false,
      message: "No face detected",
    };
  }

  const { box, score } = detection.detection;
  const landmarks = detection.landmarks;

  const videoWidth = video.videoWidth;
  const videoHeight = video.videoHeight;

  // Face Size
  const faceRatio = box.height / videoHeight;

  if (faceRatio < 0.28) {
    return {
      valid: false,
      message: "Move closer to the camera"
    };
  }

  if (faceRatio > 0.85) {
    return {
      valid: false,
      message: "Move slightly back from the camera"
    };
  }

  // Face Center
  const faceCenterX = box.x + box.width / 2;
  const faceCenterY = box.y + box.height / 2;

  const centerX = videoWidth / 2;
  const centerY = videoHeight / 2;

  const offsetX = Math.abs(faceCenterX - centerX) / videoWidth;

  const offsetY = Math.abs(faceCenterY - centerY) / videoHeight;

  if (offsetX > 0.25 || offsetY > 0.25) {
    return {
      valid: false,
      message: "Center your face",
    };
  }

  // Eye Alignment
  const leftEye = landmarks.getLeftEye();

  const rightEye = landmarks.getRightEye();

  const leftEyeCenter = {
    x: leftEye.reduce((s, p) => s + p.x, 0) / leftEye.length,
    y: leftEye.reduce((s, p) => s + p.y, 0) / leftEye.length,
  };

  const rightEyeCenter = {
    x: rightEye.reduce((s, p) => s + p.x, 0) / rightEye.length,
    y: rightEye.reduce((s, p) => s + p.y, 0) / rightEye.length,
  };

  const angle =
    Math.atan2(
      rightEyeCenter.y - leftEyeCenter.y,
      rightEyeCenter.x - leftEyeCenter.x,
    ) *
    (180 / Math.PI);

  if (Math.abs(angle) > 10) {
    return {
      valid: false,
      message: "Look straight",
    };
  }

  return {
    valid: true,
    score,
    angle,
    faceRatio,
  };
};

export default FaceAttendanceTracker;